import {
  GetEntitiesPayload,
  RetrieveEntityTxnsPayload,
  RetrieveEntityActionEventsPayload,
} from 'app/modules/entities/requests';
import { EntitySearchPayload } from 'app/modules/search/payload';
import { PaginationPayload } from 'app/shared/pagination/models';
import { GetSARsPayload } from 'app/modules/fincenSAR/requests';

const BASE = ['entities'] as const;

export const ENTITY_QUERY_KEYS = {
  getEntities: (
    payload: GetEntitiesPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, payload, useCacheEndpoint] as const;
    }
    return [...BASE, payload] as const;
  },
  getEntityDetails: (
    entityId: string,
    useCacheEndpoint: boolean = false,
    isExternalEntityId: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...BASE,
        'entity-details',
        entityId,
        useCacheEndpoint,
        isExternalEntityId,
      ] as const;
    }
    return [...BASE, 'entity-details', entityId, isExternalEntityId] as const;
  },
  getTransactionHistogram: (
    entityId: string,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, 'txn-histogram', entityId, useCacheEndpoint] as const;
    }
    return [...BASE, 'txn-histogram', entityId] as const;
  },
  getTransactionSummary: (
    entityId: string,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...BASE,
        'transaction-summary',
        entityId,
        useCacheEndpoint,
      ] as const;
    }
    return [...BASE, 'transaction-summary', entityId] as const;
  },
  getTransactions: (
    args: RetrieveEntityTxnsPayload & { entityId: string },
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, 'transactions', args, useCacheEndpoint] as const;
    }
    return [...BASE, 'transactions', args] as const;
  },
  getActionEventsHistogram: (
    entityId: string,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...BASE,
        'action-events-histogram',
        entityId,
        useCacheEndpoint,
      ] as const;
    }
    return [...BASE, 'action-events-histogram', entityId] as const;
  },
  getActionEvents: (
    args: RetrieveEntityActionEventsPayload & { entityId: string },
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [...BASE, 'action-events', args, useCacheEndpoint] as const;
    }
    return [...BASE, 'action-events', args] as const;
  },
  getEntityAlertsAll: (entityExternalId: string) =>
    [...BASE, 'entity-alerts', entityExternalId] as const,
  getEntityAlerts: (
    entityExternalId: string,
    payload: PaginationPayload,
    useCacheEndpoint: boolean = false,
  ) => {
    if (useCacheEndpoint) {
      return [
        ...ENTITY_QUERY_KEYS.getEntityAlertsAll(entityExternalId),
        payload,
        useCacheEndpoint,
      ] as const;
    }
    return [
      ...ENTITY_QUERY_KEYS.getEntityAlertsAll(entityExternalId),
      payload,
    ] as const;
  },
  getEntitySars: (args: GetSARsPayload, useCacheEndpoint: boolean = false) => {
    if (useCacheEndpoint) {
      return [...BASE, 'entity-sars', args, useCacheEndpoint] as const;
    }
    return [...BASE, 'entity-sars', args] as const;
  },
  getEntitiesConsortiumHits: (entityExternalIds: string[]) =>
    [...BASE, 'consortium', entityExternalIds] as const,
  getEntityBlockedReasons: (externalId: string) =>
    [...BASE, 'blocked-reasons', externalId] as const,
  getPaginatedSilencedRules: (
    externalID: string,
    payload?: PaginationPayload,
  ) => {
    if (payload === undefined) {
      return [...BASE, 'silenced-rules', externalID] as const;
    }
    return [...BASE, 'silenced-rules', externalID, payload] as const;
  },
  getEntityNotes: (entityId: string) =>
    [...BASE, 'entity-notes', entityId] as const,
  getEntityNarratives: (entityId: string) =>
    [...BASE, 'entity-narratives', entityId] as const,
  getEntityGeolocations: (entityId: string) =>
    [...BASE, 'geolocations', entityId] as const,
  getEntitySearch: (payload?: EntitySearchPayload) =>
    payload
      ? ([...BASE, 'search', payload] as const)
      : ([...BASE, 'search'] as const),
};
